import { Inject, Optional, Pipe, PipeTransform } from '@angular/core';
import { digger } from '@cbre/digger';
import {
  boolToString,
  formatDate,
  formatNumber,
  MiqMoment,
  EMPTY_STRING,
  DASH,
  UserSettings,
  MIQQLParser,
} from '@miq/utils';
import { getLovValueKey } from '../utils/lov-datakey-factory';
import { Column, DataType, hasReadPath } from '../models';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

/**
 * Review if value is not null or an empty string.
 *
 * @param val Value to review.
 */
export const simpleValueValidation: (val) => boolean = val => val != null && `${val}`.trim() !== '';

export const dateSanitizer = (value: string, toUtc?: boolean, extended?: boolean) => {
  const isValid = value != null && value !== '';
  const extendedFormat = extended ? 'h:mm:ss a' : '';
  let result = '';

  if (isValid) {
    const formattedDate = MiqMoment.formatFromInputFormat(
      value,
      MiqMoment.utcInputFormat,
      `MM/DD/YYYY ${extendedFormat}`,
      toUtc
    );
    const outputFormat = UserSettings.getGlobalDateDisplayFormat(undefined, extended);
    result = formatDate(formattedDate, outputFormat, 'en-US') as string;
  }
  return result;
};

const SANITIZER_MAP_BY_DATA_TYPE = {
  [DataType.Date]: (value, opts?) => dateSanitizer(value, opts.dataType !== 'timestamp with time zone'),
  [DataType.Timestamp]: (value, opts?) => dateSanitizer(value, opts.dataType !== 'timestamp with time zone'),
  [DataType.TimestampWithTimeZone]: (value, opts?) =>
    dateSanitizer(value, opts.dataType !== 'timestamp with time zone'),

  boolean: (value, opts?) => (opts.values ? value : boolToString(value)),
  integer: (value, opts?, defaultText?) =>
    formatNumber(
      value,
      'en-US',
      !!opts.options && !!opts.options.format ? opts.options.format : undefined,
      defaultText
    ),
  numeric: (value, opts?, defaultText?) =>
    formatNumber(
      value,
      'en-US',
      !!opts.options && !!opts.options.format ? opts.options.format : undefined,
      defaultText
    ),
};

const SANITIZER_MAP_BY_INPUT_TYPE = {
  Date: (value, opts?) => dateSanitizer(value, opts.dataType !== 'timestamp with time zone'),
};

const FALLBACK_LOGGER: CbreLogger = {
  info: () => {},
  debug: () => {},
  warn: () => {},
  error: () => {},
};

const formatLoggerErrorMessage = (message: string, error?: unknown) =>
  error == null ? message : `${message}: ${String(error)}`;

let dataColumnFormatterLogger: CbreLogger = FALLBACK_LOGGER;

const logDataColumnFormatterError = (message: string, error?: unknown) =>
  dataColumnFormatterLogger.error(formatLoggerErrorMessage(message, error));

const setDataColumnFormatterLogger = (logger?: CbreLogger) => {
  dataColumnFormatterLogger = logger ?? FALLBACK_LOGGER;
};

const self = s => s;

export const sanitizeValue = (value, { dataType, inputType, values, options }: any, defaultText?) => {
  const sanitizer = SANITIZER_MAP_BY_INPUT_TYPE[inputType] || SANITIZER_MAP_BY_DATA_TYPE[dataType] || self;
  return sanitizer(value, { dataType, values, options }, defaultText);
};

type DCFColumn = Pick<
  Column,
  | 'header'
  | 'field'
  | 'path'
  | 'getReadPath'
  | 'condition'
  | 'values'
  | 'lookupValueKey'
  | 'dataType'
  | 'displayFn'
  | 'inputType'
  | 'options'
> & {
  rawData?: boolean;
  filterDuplicates?: boolean;
  replaceEmptyWith?: any;
  settings: any;
  mappingLOV: any;
};

/**
 * @description
 *
 * If provided, runs the display function, which shall return a value to be
 * displayed. Otherwise, it will return the result of the
 * [digger]{@link digger} helper.
 *
 * @param data any contains the data to be transformed.
 * @param field {string} used to extract data using digger.
 * @param rawData {boolean} determines whether just to extract data or to sanitize also.
 * @param condition {string} applies the condition while extracting the data
 * @param column {object} contains all other fields like inputType, dataType etc.
 */
@Pipe({
  name: 'dataColumnFormatter',
  standalone: false,
})
export class DataColumnFormatterPipe implements PipeTransform {
  constructor(@Optional() @Inject(CbreLoggerToken) logger?: CbreLogger) {
    if (logger != null) {
      setDataColumnFormatterLogger(logger);
    }
  }
  public static extractValue(
    data: any,
    column: Partial<DCFColumn>,
    lovValueFormatter?: (lovVal: Record<string, any>) => string
  ): any {
    // return data as is if there is no column
    if (column == null) {
      return data;
    }

    const _valueWhenEmpty = column?.replaceEmptyWith ?? EMPTY_STRING;
    const { rawData = false, filterDuplicates = true } = column;
    let extractedData = extractData(data, column);

    if (extractedData == null) {
      return _valueWhenEmpty;
    }

    if (column.values?.length > 0) {
      const keyListAsArray = Array.isArray(extractedData) ? extractedData : [extractedData];
      const lovValues = getLOVValues(keyListAsArray, column, filterDuplicates, lovValueFormatter);
      if (lovValues.length > 0) {
        extractedData = lovValues;
      }
    }

    if (rawData) {
      return extractedData;
    }

    if (Array.isArray(extractedData)) {
      return formatExtractedDataWhenArray(extractedData, column, filterDuplicates, column?.replaceEmptyWith ?? DASH);
    }
    return sanitizeValue(extractedData, column).toString().trim() || _valueWhenEmpty;
  }

  /**
   * @param data object to get data from.
   * @param args special arguments.
   * @param _updated optional flag used to manually trigger the pipe.
   */
  transform(data: Record<string, any>, args: Partial<DCFColumn> = {}, _updated?: boolean): any {
    const { displayFn, settings = {}, mappingLOV } = args ?? {};
    try {
      if (displayFn && typeof displayFn === 'function') {
        let result = displayFn(data, settings, mappingLOV, args);
        if (result && typeof result === 'object' && result.value != null) {
          result = result.value;
        }
        return result;
      }
      return DataColumnFormatterPipe.extractValue(data, args);
    } catch (e) {
      // this catch will keep ppt download working in case any field is error
      logDataColumnFormatterError('DataColumnFormatterPipe.Transform error', e);
      return args?.replaceEmptyWith ?? EMPTY_STRING;
    }
  }
}

const getLOVValues = (
  keyList: string[],
  column: Partial<DCFColumn>,
  filterDuplicates: boolean,
  lovValueFormatter: Function
) => {
  // Get the Lov key could be key or value depending on the Column
  const dataKey = getLovValueKey(column);
  // Get the list of the values from the LOV options
  return filterDuplicates
    ? getUniqueLOVValues(keyList, dataKey, column, lovValueFormatter)
    : getAllLovValues(keyList, dataKey, column, lovValueFormatter, column?.replaceEmptyWith ?? DASH);
};

const getUniqueLOVValues = (
  keyList: any[],
  dataKey: string,
  column: Partial<DCFColumn>,
  lovValueFormatter: Function
) => {
  const extractedDataMap = Object.assign({}, ...keyList.map(val => ({ [val]: true })));

  return column.values
    .filter(val => val[dataKey] != null && extractedDataMap[val[dataKey]])
    .map(val => (lovValueFormatter ? lovValueFormatter(val) : val.value));
};

const getAllLovValues = (
  keyList: any[],
  dataKey: string,
  column: Partial<DCFColumn>,
  lovValueFormatter: Function,
  valueWhenEmpty = EMPTY_STRING
) => {
  // Make a mapping of the available LOVs
  const availableLOVsMap = Object.assign(
    {},
    ...(column.values ?? []).map(lovItem => ({ [lovItem[dataKey]]: lovItem }))
  );

  return keyList.map(keyItem =>
    lovValueFormatter
      ? lovValueFormatter(availableLOVsMap[keyItem])
      : (availableLOVsMap[keyItem]?.value ?? valueWhenEmpty)
  );
};

const formatExtractedDataWhenArray = (
  extractedData: (string | number | Date | boolean)[],
  column: Partial<DCFColumn>,
  filterDuplicates: boolean,
  valueWhenEmpty = EMPTY_STRING
) => {
  if (filterDuplicates) {
    const isNotNullNoEmpty = val => val != null && val !== EMPTY_STRING;
    return Array.from(new Set(extractedData.filter(isNotNullNoEmpty).map(value => sanitizeValue(value, column))))
      .sort((a, b) => {
        if (typeof a === 'number' && typeof b === 'number') {
          return a - b;
        }
        if (a instanceof Date && b instanceof Date) {
          return a.getTime() - b.getTime();
        }
        return String(a).localeCompare(String(b));
      })
      .join(', ');
  }

  return Array.from(extractedData, value => sanitizeValue(value, column, valueWhenEmpty ?? DASH)).join(', ');
};

const extractData = (data: any, column: Partial<DCFColumn>) => {
  let extractedData: any;
  const { field = '', condition } = column;

  if (!column.path && simpleValueValidation(condition)) {
    let parsedCondition;
    try {
      parsedCondition = MIQQLParser.parse(condition);
      extractedData = digger(data, field, parsedCondition);
    } catch (e) {
      if (parsedCondition != null) {
        logDataColumnFormatterError(
          `Something went wrong while extracting data from ${field} with condition ${condition}`,
          e
        );
        extractedData = digger(data, field);
      } else {
        logDataColumnFormatterError(
          // tslint:disable-next-line:max-line-length
          `Something went wrong while parsing the catalog condition for\n - field: '${column.header}'\n - condition: '${condition}'`,
          e
        );
      }
    }
  } else {
    // prevent failure of unimplemented ColumnFactory
    if (!column.getReadPath) {
      column = Object.assign(column, hasReadPath());
    }
    extractedData = digger(data, column.getReadPath());
  }

  return extractedData;
};
